import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  CircularProgress,
  Alert,
  Snackbar,
  Tooltip,
  InputAdornment,
  List,
  ListItem,
  Divider,
  useMediaQuery
} from '@mui/material';
import NotificationCardView from '../../../Components/NotificationCardView/NotificationCardView';
import SearchIcon from '@mui/icons-material/Search';
import {
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  DeleteSweep as DeleteSweepIcon,
  MarkEmailRead as MarkEmailReadIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  limit,
  writeBatch,
  Timestamp
} from 'firebase/firestore';
import { DATABASE, AUTH_LOGIN } from '../../../Config/firebase';
import FUNCTIONS_URLS from '../../../Config/functions';
import { EMAIL_TEMPLATES } from '../../../Config/emailTemplates';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

async function adminJsonHeaders() {
  const user = AUTH_LOGIN.currentUser;
  if (!user) {
    throw new Error('Sign in as an admin to send');
  }
  const token = await user.getIdToken();
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

const NotificationCenter = () => {
  const [tabValue, setTabValue] = useState(0);
  const [clientNotifications, setClientNotifications] = useState([]);
  const [adminAlerts, setAdminAlerts] = useState([]);
  const [scheduledEmails, setScheduledEmails] = useState([]);
  const [recipientMap, setRecipientMap] = useState({});
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [markAllLoading, setMarkAllLoading] = useState(false);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailFormLoading, setEmailFormLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [userSearch, setUserSearch] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [notificationToEdit, setNotificationToEdit] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', body: '' });
  
  // Detect mobile device (breakpoint at 960px)
  const isMobile = useMediaQuery("(max-width: 960px)");

  // Form state for custom notification
  const [notificationForm, setNotificationForm] = useState({
    title: '',
    body: '',
    selectedUsers: [],
    sendPush: true
  });

  // Form state for custom email & notification
  const [emailForm, setEmailForm] = useState({
    email_subject: '',
    email_body: '',
    notification_title: '',
    notification_body: '',
    selectedUsers: [],
    send_push: true,
    schedule_mode: 'now',
    schedule_at: null,
    txn_input: '',
    txn_preview: null,
    selected_template: 'none'
  });
  const [emailFormUserSearch, setEmailFormUserSearch] = useState('');

  // Fetch users for custom notification
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const usersQuery = query(
          collection(DATABASE, 'users'),
          where('role', '==', 1), // Only clients
          orderBy('full_name')
        );
        const snapshot = await getDocs(usersQuery);
        const usersData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setUsers(usersData);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };

    fetchUsers();
  }, []);

  // Listen to client notifications
  useEffect(() => {
    const notificationsQuery = query(
      collection(DATABASE, 'notifications'),
      orderBy('date', 'desc'),
      limit(100)
    );

    const unsubscribe = onSnapshot(notificationsQuery, async (snapshot) => {
      const notificationsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Fetch missing user names/emails for old notifications
      const notificationsWithUsers = await Promise.all(
        notificationsData.map(async (notification) => {
          if (!notification.user_name || notification.user_name === 'Unknown') {
            if (notification.user_id) {
              try {
                const userDoc = await getDoc(doc(DATABASE, 'users', notification.user_id));
                if (userDoc.exists()) {
                  const userData = userDoc.data();
                  // Update notification in Firestore with user data
                  await updateDoc(doc(DATABASE, 'notifications', notification.id), {
                    user_name: userData.full_name || 'Unknown',
                    user_email: userData.email || ''
                  });
                  return {
                    ...notification,
                    user_name: userData.full_name || 'Unknown',
                    user_email: userData.email || ''
                  };
                }
              } catch (error) {
                console.error('Error fetching user data:', error);
              }
            }
          }
          return notification;
        })
      );

      setClientNotifications(notificationsWithUsers);
    }, (error) => {
      console.error('Error fetching notifications:', error);
    });

    return () => unsubscribe();
  }, []);

  // Listen to admin alerts
  useEffect(() => {
    const alertsQuery = query(
      collection(DATABASE, 'admin_alerts'),
      orderBy('created_at', 'desc'),
      limit(100)
    );

    const unsubscribe = onSnapshot(alertsQuery, (snapshot) => {
      const alertsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAdminAlerts(alertsData);
    }, (error) => {
      console.error('Error fetching admin alerts:', error);
    });

    return () => unsubscribe();
  }, []);

  // Build recipient map from users (for Scheduled tab)
  useEffect(() => {
    const map = {};
    users.forEach(u => {
      map[u.id] = { full_name: u.full_name || 'Unknown', email: u.email || '' };
    });
    setRecipientMap(prev => ({ ...prev, ...map }));
  }, [users]);

  // Listen to scheduled custom emails
  useEffect(() => {
    const scheduledQuery = query(
      collection(DATABASE, 'scheduled_custom_emails'),
      orderBy('scheduled_at', 'desc'),
      limit(100)
    );

    const unsubscribe = onSnapshot(scheduledQuery, (snapshot) => {
      const data = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data()
      }));
      setScheduledEmails(data);
    }, (error) => {
      console.error('Error fetching scheduled emails:', error);
    });

    return () => unsubscribe();
  }, []);

  // Fetch missing recipients for scheduled emails (users not in client list)
  useEffect(() => {
    const userIds = new Set();
    scheduledEmails.forEach(item => (item.user_ids || []).forEach(uid => userIds.add(uid)));
    const missing = [...userIds].filter(uid => !recipientMap[uid]);
    if (missing.length === 0) return;
    let cancelled = false;
    (async () => {
      const updates = {};
      for (const uid of missing) {
        try {
          const userDoc = await getDoc(doc(DATABASE, 'users', uid));
          if (cancelled) return;
          if (userDoc.exists()) {
            const d = userDoc.data();
            updates[uid] = { full_name: d.full_name || 'Unknown', email: d.email || '' };
          } else {
            updates[uid] = { full_name: 'Unknown', email: '' };
          }
        } catch {
          updates[uid] = { full_name: 'Unknown', email: '' };
        }
      }
      if (!cancelled) setRecipientMap(prev => ({ ...prev, ...updates }));
    })();
    return () => { cancelled = true; };
  }, [scheduledEmails]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleResolveAlert = async (alertId) => {
    try {
      await updateDoc(doc(DATABASE, 'admin_alerts', alertId), {
        resolved: true
      });
      setSnackbar({ open: true, message: 'Alert resolved successfully', severity: 'success' });
    } catch (error) {
      console.error('Error resolving alert:', error);
      setSnackbar({ open: true, message: 'Error resolving alert', severity: 'error' });
    }
  };

  const handleMarkAllAlertsRead = async () => {
    const unresolved = adminAlerts.filter(a => !a.resolved);
    if (unresolved.length === 0) {
      setSnackbar({ open: true, message: 'No unresolved alerts to mark', severity: 'info' });
      return;
    }


    setMarkAllLoading(true);
    try {
      const BATCH_SIZE = 500;
      for (let i = 0; i < unresolved.length; i += BATCH_SIZE) {
        const batch = writeBatch(DATABASE);
        const chunk = unresolved.slice(i, i + BATCH_SIZE);
        chunk.forEach((alert) => {
          batch.update(doc(DATABASE, 'admin_alerts', alert.id), { resolved: true });
        });
        await batch.commit();
      }
      setSnackbar({
        open: true,
        message: `${unresolved.length} alert(s) marked as read`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error marking all alerts as read:', error);
      setSnackbar({ open: true, message: 'Error marking alerts as read', severity: 'error' });
    } finally {
      setMarkAllLoading(false);
    }
  };

  const handleCleanupOldAlerts = async () => {
    const cutoffDate = new Date();
    cutoffDate.setMonth(cutoffDate.getMonth() - 1); // 1 month ago
    const cutoffTimestamp = Timestamp.fromDate(cutoffDate);

    const resolvedQuery = query(
      collection(DATABASE, 'admin_alerts'),
      where('resolved', '==', true),
      where('created_at', '<', cutoffTimestamp),
      limit(500)
    );

    setCleanupLoading(true);
    try {
      const snapshot = await getDocs(resolvedQuery);
      if (snapshot.empty) {
        setSnackbar({ open: true, message: 'No old resolved alerts to delete', severity: 'info' });
        return;
      }

      const BATCH_SIZE = 500;
      let deleted = 0;
      for (let i = 0; i < snapshot.docs.length; i += BATCH_SIZE) {
        const batch = writeBatch(DATABASE);
        const chunk = snapshot.docs.slice(i, i + BATCH_SIZE);
        chunk.forEach((d) => {
          batch.delete(doc(DATABASE, 'admin_alerts', d.id));
          deleted++;
        });
        await batch.commit();
      }

      setSnackbar({
        open: true,
        message: `Deleted ${deleted} old resolved alert(s) (older than 1 month)`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error cleaning up alerts:', error);
      setSnackbar({ open: true, message: 'Error cleaning up alerts', severity: 'error' });
    } finally {
      setCleanupLoading(false);
    }
  };

  const handleSendCustomNotification = async () => {
    if (!notificationForm.title || !notificationForm.body || notificationForm.selectedUsers.length === 0) {
      setSnackbar({ open: true, message: 'Please fill all fields and select at least one user', severity: 'warning' });
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(FUNCTIONS_URLS.sendCustomNotification, {
        method: 'POST',
        headers: await adminJsonHeaders(),
        body: JSON.stringify({
          user_ids: notificationForm.selectedUsers,
          title: notificationForm.title,
          body: notificationForm.body,
          type: 1, // Custom notification
          send_push: notificationForm.sendPush
        }),
      });

      const result = await response.json();

      if (result.success) {
        setSnackbar({ 
          open: true, 
          message: `Notification sent to ${result.results.success} user(s)`, 
          severity: 'success' 
        });
        setSendDialogOpen(false);
        setNotificationForm({ title: '', body: '', selectedUsers: [], sendPush: true });
      } else {
        throw new Error(result.error || 'Failed to send notification');
      }
    } catch (error) {
      console.error('Error sending custom notification:', error);
      setSnackbar({ open: true, message: error.message || 'Error sending notification', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 1: return 'Custom';
      case 2: return 'Transaction';
      case 3: return 'System';
      default: return 'Unknown';
    }
  };

  // Filter users based on search
  const filteredUsers = users.filter(user => {
    const searchLower = userSearch.toLowerCase();
    const name = (user.full_name || 'Unknown').toLowerCase();
    const email = (user.email || '').toLowerCase();
    return name.includes(searchLower) || email.includes(searchLower);
  });

  // Select all filtered users
  const handleSelectAll = () => {
    if (notificationForm.selectedUsers.length === filteredUsers.length) {
      // Deselect all
      setNotificationForm({ ...notificationForm, selectedUsers: [] });
    } else {
      // Select all filtered users
      const allFilteredIds = filteredUsers.map(u => u.id);
      setNotificationForm({ ...notificationForm, selectedUsers: allFilteredIds });
    }
  };

  // Toggle individual user selection
  const handleToggleUser = (userId) => {
    const current = notificationForm.selectedUsers;
    if (current.includes(userId)) {
      setNotificationForm({ ...notificationForm, selectedUsers: current.filter(id => id !== userId) });
    } else {
      setNotificationForm({ ...notificationForm, selectedUsers: [...current, userId] });
    }
  };

  // Filter users for email form
  const filteredEmailUsers = users.filter(user => {
    const searchLower = emailFormUserSearch.toLowerCase();
    const name = (user.full_name || 'Unknown').toLowerCase();
    const email = (user.email || '').toLowerCase();
    return name.includes(searchLower) || email.includes(searchLower);
  });

  const handleTxnLookup = async () => {
    const input = emailForm.txn_input?.trim();
    if (!input) {
      setSnackbar({ open: true, message: 'Enter TXN ID or doc ID', severity: 'warning' });
      return;
    }
    try {
      const docSnap = await getDoc(doc(DATABASE, 'transection_requests', input));
      const docExists = typeof docSnap.exists === 'function' ? docSnap.exists() : docSnap.exists;
      const data = docSnap.data?.();
      if (docExists && data) {
        const userId = data.user_id;
        if (!userId) {
          setSnackbar({ open: true, message: 'TXN found but has no user_id', severity: 'warning' });
          return;
        }
        const userDoc = await getDoc(doc(DATABASE, 'users', userId));
        const userExists = typeof userDoc.exists === 'function' ? userDoc.exists() : userDoc.exists;
        const userData = userExists && userDoc.data ? userDoc.data() : {};
        setEmailForm(prev => ({
          ...prev,
          selectedUsers: prev.selectedUsers.includes(userId) ? prev.selectedUsers : [...prev.selectedUsers, userId],
          txn_preview: {
            user_name: userData?.full_name || 'Unknown',
            amount: data.total_amount,
            request_type: data.request_type === 1 ? 'Withdrawal' : data.request_type === 0 ? 'Deposit' : 'Unknown',
            txn_id: data.txn_id || docSnap.id
          }
        }));
        setSnackbar({ open: true, message: 'TXN found. User selected.', severity: 'success' });
        return;
      }
      const num = Number(input);
      if (!isNaN(num)) {
        const q = query(
          collection(DATABASE, 'transection_requests'),
          where('txn_id', '==', num),
          limit(1)
        );
        const qSnap = await getDocs(q);
        if (!qSnap.empty) {
          const d = qSnap.docs[0];
          const qData = d.data?.();
          if (!qData) {
            setSnackbar({ open: true, message: 'TXN found but could not read data', severity: 'warning' });
            return;
          }
          const userId = qData.user_id;
          if (!userId) {
            setSnackbar({ open: true, message: 'TXN found but has no user_id', severity: 'warning' });
            return;
          }
          const userDoc = await getDoc(doc(DATABASE, 'users', userId));
          const userExists = typeof userDoc.exists === 'function' ? userDoc.exists() : userDoc.exists;
          const userData = userExists && userDoc.data ? userDoc.data() : {};
          setEmailForm(prev => ({
            ...prev,
            selectedUsers: prev.selectedUsers.includes(userId) ? prev.selectedUsers : [...prev.selectedUsers, userId],
            txn_preview: {
              user_name: userData?.full_name || 'Unknown',
              amount: qData.total_amount,
              request_type: qData.request_type === 1 ? 'Withdrawal' : qData.request_type === 0 ? 'Deposit' : 'Unknown',
              txn_id: qData.txn_id || d.id
            }
          }));
          setSnackbar({ open: true, message: 'TXN found. User selected.', severity: 'success' });
        } else {
          setSnackbar({ open: true, message: 'TXN not found', severity: 'warning' });
        }
      } else {
        setSnackbar({ open: true, message: 'TXN not found', severity: 'warning' });
      }
    } catch (err) {
      console.error('TXN lookup error:', err);
      setSnackbar({ open: true, message: 'Error looking up TXN', severity: 'error' });
    }
  };

  const handleSendCustomEmail = async () => {
    if (!emailForm.email_subject || !emailForm.email_body || !emailForm.notification_title || !emailForm.notification_body) {
      setSnackbar({ open: true, message: 'Please fill all required fields', severity: 'warning' });
      return;
    }
    if (emailForm.selectedUsers.length === 0) {
      setSnackbar({ open: true, message: 'Select at least one user', severity: 'warning' });
      return;
    }
    if (emailForm.schedule_mode === 'schedule' && !emailForm.schedule_at) {
      setSnackbar({ open: true, message: 'Select date and time for schedule', severity: 'warning' });
      return;
    }
    setEmailFormLoading(true);
    try {
      const payload = {
        user_ids: emailForm.selectedUsers,
        email_subject: emailForm.email_subject,
        email_body: emailForm.email_body,
        notification_title: emailForm.notification_title,
        notification_body: emailForm.notification_body,
        send_push: emailForm.send_push,
      };
      if (emailForm.txn_preview && emailForm.txn_input) {
        payload.txn_doc_id = emailForm.txn_input.trim();
      }
      if (emailForm.schedule_mode === 'schedule' && emailForm.schedule_at) {
        payload.schedule_at = emailForm.schedule_at.toISOString();
      }
      const res = await fetch(FUNCTIONS_URLS.sendCustomEmailAndNotification, {
        method: 'POST',
        headers: await adminJsonHeaders(),
        body: JSON.stringify(payload)
      });
      const result = await res.json();
      if (result.success) {
        setSnackbar({
          open: true,
          message: result.scheduled
            ? `Scheduled for ${emailForm.schedule_at?.format?.('DD/MM/YYYY HH:mm') || emailForm.schedule_at}`
            : `Sent to ${result.results?.success ?? 0} user(s)`,
          severity: 'success'
        });
        setEmailDialogOpen(false);
        setEmailForm({
          email_subject: '',
          email_body: '',
          notification_title: '',
          notification_body: '',
          selectedUsers: [],
          send_push: true,
          schedule_mode: 'now',
          schedule_at: null,
          txn_input: '',
          txn_preview: null
        });
        setEmailFormUserSearch('');
      } else {
        throw new Error(result.error || 'Failed to send');
      }
    } catch (err) {
      console.error('Send custom email error:', err);
      setSnackbar({ open: true, message: err.message || 'Error sending', severity: 'error' });
    } finally {
      setEmailFormLoading(false);
    }
  };

  const handleSelectAllEmailUsers = () => {
    if (emailForm.selectedUsers.length === filteredEmailUsers.length) {
      setEmailForm({ ...emailForm, selectedUsers: [] });
    } else {
      setEmailForm({ ...emailForm, selectedUsers: filteredEmailUsers.map(u => u.id) });
    }
  };

  const handleToggleEmailUser = (userId) => {
    const current = emailForm.selectedUsers;
    if (current.includes(userId)) {
      setEmailForm({ ...emailForm, selectedUsers: current.filter(id => id !== userId) });
    } else {
      setEmailForm({ ...emailForm, selectedUsers: [...current, userId] });
    }
  };

  // Handle message click
  const handleMessageClick = (notification) => {
    setSelectedMessage(notification);
    setMessageDialogOpen(true);
  };

  // Handle delete notification
  const handleDeleteNotification = async (notificationId) => {
    if (!window.confirm('Are you sure you want to delete this notification?')) {
      return;
    }

    try {
      await deleteDoc(doc(DATABASE, 'notifications', notificationId));
      setSnackbar({ open: true, message: 'Notification deleted successfully', severity: 'success' });
    } catch (error) {
      console.error('Error deleting notification:', error);
      setSnackbar({ open: true, message: 'Error deleting notification', severity: 'error' });
    }
  };

  // Handle edit notification
  const handleEditNotification = (notification) => {
    setNotificationToEdit(notification);
    setEditForm({ title: notification.title, body: notification.body });
    setEditDialogOpen(true);
  };

  // Save edited notification
  const handleSaveEdit = async () => {
    if (!notificationToEdit || !editForm.title || !editForm.body) {
      setSnackbar({ open: true, message: 'Please fill all fields', severity: 'warning' });
      return;
    }

    try {
      await updateDoc(doc(DATABASE, 'notifications', notificationToEdit.id), {
        title: editForm.title,
        body: editForm.body
      });
      setSnackbar({ open: true, message: 'Notification updated successfully', severity: 'success' });
      setEditDialogOpen(false);
      setNotificationToEdit(null);
      setEditForm({ title: '', body: '' });
    } catch (error) {
      console.error('Error updating notification:', error);
      setSnackbar({ open: true, message: 'Error updating notification', severity: 'error' });
    }
  };

  // Fetch user name/email if missing (for old notifications)
  const getUserDisplay = async (notification) => {
    if (notification.user_name && notification.user_name !== 'Unknown') {
      return { name: notification.user_name, email: notification.user_email || '' };
    }

    // Try to fetch from user document
    if (notification.user_id) {
      try {
        const userDoc = await getDoc(doc(DATABASE, 'users', notification.user_id));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          // Update notification with user data
          await updateDoc(doc(DATABASE, 'notifications', notification.id), {
            user_name: userData.full_name || 'Unknown',
            user_email: userData.email || ''
          });
          return { name: userData.full_name || 'Unknown', email: userData.email || '' };
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    }

    return { name: 'Unknown', email: '' };
  };

  return (
    <Box sx={{ 
      p: { xs: 2, md: 3 },
      paddingTop: { xs: '80px', md: '24px' }, // Espaço para evitar sobreposição do TopHeader em mobile
      bgcolor: "background.default", // Garante background escuro
    }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', md: 'center' }, 
        mb: 3,
        gap: { xs: 2, md: 0 }
      }}>
        <Typography variant="h4" sx={{ fontWeight: 600, fontSize: { xs: '24px', md: '32px' } }}>
          Notification Center
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={() => setSendDialogOpen(true)}
            sx={{
              background: 'linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)',
              width: { xs: '100%', md: 'auto' },
              '&:hover': { background: 'linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)', opacity: 0.9 }
            }}
          >
            Send Custom Notification
          </Button>
          <Button
            variant="contained"
            startIcon={<MarkEmailReadIcon />}
            onClick={() => setEmailDialogOpen(true)}
            sx={{
              background: 'linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)',
              width: { xs: '100%', md: 'auto' },
              '&:hover': { background: 'linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)', opacity: 0.9 }
            }}
          >
            Send Custom Email & Notification
          </Button>
        </Box>
      </Box>

      <Paper sx={{ width: '100%', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label={`Client Notifications (${clientNotifications.length})`} />
          <Tab label={`Admin Alerts (${adminAlerts.filter(a => !a.resolved).length})`} />
          <Tab
            icon={<ScheduleIcon fontSize="small" />}
            iconPosition="start"
            label={`Scheduled Emails & Notifications (${scheduledEmails.length})`}
          />
        </Tabs>

        {/* Client Notifications Tab */}
        {tabValue === 0 && (
          <>
            {/* Desktop: Table (comportamento atual) */}
            {!isMobile && (
              <TableContainer sx={{ maxHeight: 600 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Date</strong></TableCell>
                      <TableCell><strong>User</strong></TableCell>
                      <TableCell><strong>Title</strong></TableCell>
                      <TableCell><strong>Message</strong></TableCell>
                      <TableCell><strong>Type</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {clientNotifications.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography color="text.secondary">No notifications found</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      clientNotifications.map((notification) => (
                        <TableRow key={notification.id} hover>
                          <TableCell>{formatDate(notification.date)}</TableCell>
                          <TableCell>
                            <Typography variant="body2">{notification.user_name || 'Unknown'}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {notification.user_email}
                            </Typography>
                          </TableCell>
                          <TableCell>{notification.title}</TableCell>
                          <TableCell sx={{ maxWidth: 300 }}>
                            <Typography 
                              variant="body2" 
                              noWrap={notification.body.length < 100}
                              onClick={() => notification.body.length > 100 && handleMessageClick(notification)}
                              sx={{ 
                                cursor: notification.body.length > 100 ? 'pointer' : 'default',
                                '&:hover': notification.body.length > 100 ? { textDecoration: 'underline' } : {}
                              }}
                            >
                              {notification.body}
                              {notification.body.length > 100 && '... (click to view full)'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={getTypeLabel(notification.type)} size="small" />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={notification.read ? 'Read' : 'Unread'}
                              size="small"
                              color={notification.read ? 'default' : 'primary'}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleEditNotification(notification)}
                                title="Edit notification"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteNotification(notification.id)}
                                title="Delete notification"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            
            {/* Mobile: Cards (nova visualização) */}
            {isMobile && (
              <NotificationCardView
                notifications={clientNotifications}
                loading={loading}
                onEdit={handleEditNotification}
                onDelete={handleDeleteNotification}
                onView={handleMessageClick}
                formatDate={formatDate}
                getTypeLabel={getTypeLabel}
                type="client"
              />
            )}
          </>
        )}

        {/* Admin Alerts Tab */}
        {tabValue === 1 && (
          <>
            {/* Admin Alerts action bar */}
            <Box sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, borderBottom: 1, borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {adminAlerts.some(a => !a.resolved) && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={markAllLoading ? <CircularProgress size={18} /> : <CheckCircleIcon />}
                    onClick={handleMarkAllAlertsRead}
                    disabled={markAllLoading}
                    sx={{ textTransform: 'none' }}
                  >
                    Mark all as read
                  </Button>
                )}
                <Tooltip title="Delete resolved alerts older than 1 month to save Firebase storage">
                  <span>
                    <Button
                      variant="outlined"
                      size="small"
                      color="secondary"
                      startIcon={cleanupLoading ? <CircularProgress size={18} /> : <DeleteSweepIcon />}
                      onClick={handleCleanupOldAlerts}
                      disabled={cleanupLoading}
                      sx={{ textTransform: 'none' }}
                    >
                      Cleanup old alerts
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </Box>
            {/* Desktop: Table (comportamento atual) */}
            {!isMobile && (
              <TableContainer sx={{ maxHeight: 600 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Date</strong></TableCell>
                      <TableCell><strong>Title</strong></TableCell>
                      <TableCell><strong>Message</strong></TableCell>
                      <TableCell><strong>Severity</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {adminAlerts.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography color="text.secondary">No alerts found</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      adminAlerts.map((alert) => (
                        <TableRow 
                          key={alert.id} 
                          hover
                          sx={{ 
                            bgcolor: alert.resolved ? 'transparent' : 'rgba(255, 152, 0, 0.05)',
                            opacity: alert.resolved ? 0.6 : 1
                          }}
                        >
                          <TableCell>{formatDate(alert.created_at)}</TableCell>
                          <TableCell>{alert.title}</TableCell>
                          <TableCell sx={{ maxWidth: 350 }}>
                            <Typography variant="body2">
                              {alert.message}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={alert.severity}
                              size="small"
                              color={getSeverityColor(alert.severity)}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={alert.resolved ? 'Resolved' : 'Active'}
                              size="small"
                              color={alert.resolved ? 'default' : 'warning'}
                            />
                          </TableCell>
                          <TableCell>
                            {!alert.resolved && (
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleResolveAlert(alert.id)}
                                title="Mark as resolved"
                              >
                                <CheckCircleIcon />
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            
            {/* Mobile: Cards (nova visualização) */}
            {isMobile && (
              <NotificationCardView
                notifications={adminAlerts}
                loading={loading}
                onDelete={handleDeleteNotification}
                onResolve={handleResolveAlert}
                formatDate={formatDate}
                getSeverityColor={getSeverityColor}
                type="admin"
              />
            )}
          </>
        )}

        {/* Scheduled Emails & Notifications Tab */}
        {tabValue === 2 && (
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader size={isMobile ? 'small' : 'medium'}>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Scheduled At</strong></TableCell>
                  <TableCell><strong>Subject</strong></TableCell>
                  <TableCell><strong>Recipients</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Sent At</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {scheduledEmails.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography color="text.secondary">
                        No scheduled emails. Use &quot;Send Custom Email & Notification&quot; and choose &quot;Schedule for&quot; to create one.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  scheduledEmails.map((item) => {
                    const recipients = (item.user_ids || []).map(uid => {
                      const r = recipientMap[uid];
                      if (!r) return `ID: ${uid}`;
                      return r.email ? `${r.full_name} (${r.email})` : r.full_name;
                    });
                    const display = recipients.length ? recipients : ['—'];
                    return (
                      <TableRow key={item.id} hover>
                        <TableCell>
                          {item.scheduled_at?.toDate
                            ? formatDate(item.scheduled_at)
                            : 'N/A'}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 200 }}>
                          <Typography variant="body2" noWrap title={item.email_subject}>
                            {item.email_subject || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 280 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                            {display.map((txt, i) => (
                              <Typography key={i} variant="body2" title={txt}>
                                {txt}
                              </Typography>
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={item.sent ? 'Sent' : 'Pending'}
                            size="small"
                            color={item.sent ? 'success' : 'warning'}
                          />
                        </TableCell>
                        <TableCell>
                          {item.sent_at?.toDate
                            ? formatDate(item.sent_at)
                            : '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Send Custom Notification Dialog */}
      <Dialog 
        open={sendDialogOpen} 
        onClose={() => {
          setSendDialogOpen(false);
          setNotificationForm({ title: '', body: '', selectedUsers: [], sendPush: true });
          setUserSearch('');
        }} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>Send Custom Notification</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Title"
              fullWidth
              value={notificationForm.title}
              onChange={(e) => setNotificationForm({ ...notificationForm, title: e.target.value })}
            />
            <TextField
              label="Message"
              fullWidth
              multiline
              rows={4}
              value={notificationForm.body}
              onChange={(e) => setNotificationForm({ ...notificationForm, body: e.target.value })}
            />
            <FormControl fullWidth>
              <Box sx={{ mb: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search users by name or email..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              <Box sx={{ 
                border: '1px solid #ccc', 
                borderRadius: 1, 
                maxHeight: 300, 
                overflow: 'auto',
                p: 1
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, pb: 1, borderBottom: '1px solid #eee' }}>
                  <Checkbox
                    checked={filteredUsers.length > 0 && notificationForm.selectedUsers.length === filteredUsers.length}
                    indeterminate={notificationForm.selectedUsers.length > 0 && notificationForm.selectedUsers.length < filteredUsers.length}
                    onChange={handleSelectAll}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Select All ({filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''})
                  </Typography>
                  <Typography variant="caption" sx={{ ml: 'auto', color: 'text.secondary' }}>
                    {notificationForm.selectedUsers.length} selected
                  </Typography>
                </Box>
                <List dense sx={{ maxHeight: 250, overflow: 'auto' }}>
                  {filteredUsers.length === 0 ? (
                    <ListItem>
                      <Typography variant="body2" color="text.secondary">
                        No users found
                      </Typography>
                    </ListItem>
                  ) : (
                    filteredUsers.map((user) => (
                      <ListItem 
                        key={user.id} 
                        button
                        onClick={() => handleToggleUser(user.id)}
                        sx={{
                          borderRadius: 1,
                          mb: 0.5,
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                      >
                        <Checkbox 
                          checked={notificationForm.selectedUsers.includes(user.id)}
                          onChange={() => handleToggleUser(user.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <ListItemText 
                          primary={user.full_name || 'Unknown'} 
                          secondary={user.email}
                        />
                      </ListItem>
                    ))
                  )}
                </List>
              </Box>
            </FormControl>
            <FormControl fullWidth>
              <Tooltip 
                title="If checked, users with FCM tokens will receive a push notification on their mobile device. The in-app notification will always be created regardless of this setting."
                arrow
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Checkbox
                    checked={notificationForm.sendPush}
                    onChange={(e) => setNotificationForm({ ...notificationForm, sendPush: e.target.checked })}
                  />
                  <Typography>
                    Send push notification (if user has FCM token)
                    <Typography component="span" sx={{ ml: 0.5, color: 'text.secondary', fontSize: '0.75rem' }}>
                      (ℹ️ hover for details)
                    </Typography>
                  </Typography>
                </Box>
              </Tooltip>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setSendDialogOpen(false);
            setNotificationForm({ title: '', body: '', selectedUsers: [], sendPush: true });
            setUserSearch('');
          }}>Cancel</Button>
          <Button
            onClick={handleSendCustomNotification}
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
            sx={{
              background: 'linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)',
            }}
          >
            {loading ? 'Sending...' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send Custom Email & Notification Dialog */}
      <Dialog
        open={emailDialogOpen}
        onClose={() => {
          setEmailDialogOpen(false);
          setEmailForm({
            email_subject: '',
            email_body: '',
            notification_title: '',
            notification_body: '',
            selectedUsers: [],
            send_push: true,
            schedule_mode: 'now',
            schedule_at: null,
            txn_input: '',
            txn_preview: null,
            selected_template: 'none'
          });
          setEmailFormUserSearch('');
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Send Custom Email & Notification</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Template</InputLabel>
              <Select
                value={emailForm.selected_template}
                label="Template"
                onChange={(e) => {
                  const templateId = e.target.value;
                  const template = EMAIL_TEMPLATES.find(t => t.id === templateId);
                  setEmailForm(prev => ({
                    ...prev,
                    selected_template: templateId,
                    ...(template && templateId !== 'none' ? {
                      email_subject: template.subject,
                      email_body: template.body,
                      notification_title: template.notification_title,
                      notification_body: template.notification_body
                    } : {})
                  }));
                }}
              >
                {EMAIL_TEMPLATES.map((t) => (
                  <MenuItem key={t.id} value={t.id}>{t.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
              <TextField
                label="TXN (optional)"
                size="small"
                placeholder="Doc ID or txn_id"
                value={emailForm.txn_input}
                onChange={(e) => setEmailForm({ ...emailForm, txn_input: e.target.value, txn_preview: null })}
                sx={{ flex: 1 }}
              />
              <Button variant="outlined" size="medium" onClick={handleTxnLookup}>
                Buscar
              </Button>
            </Box>
            {emailForm.txn_preview && (
              <Alert severity="info" sx={{ py: 0.5 }}>
                Cliente: {emailForm.txn_preview.user_name} | Valor: ${Number(emailForm.txn_preview.amount || 0).toFixed(2)} | Tipo: {emailForm.txn_preview.request_type}
              </Alert>
            )}
            <Typography variant="caption" color="text.secondary">
              Placeholders: {`{{user_name}}`} {`{{amount}}`} {`{{txn_id}}`} {`{{request_type}}`} {`{{request_status_label}}`}
            </Typography>
            <TextField label="Email Subject" fullWidth required value={emailForm.email_subject} onChange={(e) => setEmailForm({ ...emailForm, email_subject: e.target.value })} />
            <TextField label="Email Body (HTML)" fullWidth multiline rows={8} required value={emailForm.email_body} onChange={(e) => setEmailForm({ ...emailForm, email_body: e.target.value })} />
            <TextField label="Notification Title" fullWidth required value={emailForm.notification_title} onChange={(e) => setEmailForm({ ...emailForm, notification_title: e.target.value })} />
            <TextField label="Notification Body" fullWidth multiline rows={4} required value={emailForm.notification_body} onChange={(e) => setEmailForm({ ...emailForm, notification_body: e.target.value })} />
            <FormControl fullWidth>
              <TextField
                fullWidth
                size="small"
                placeholder="Search users..."
                value={emailFormUserSearch}
                onChange={(e) => setEmailFormUserSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
              />
              <Box sx={{ border: '1px solid #ccc', borderRadius: 1, maxHeight: 200, overflow: 'auto', p: 1, mt: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, pb: 1, borderBottom: '1px solid #eee' }}>
                  <Checkbox
                    checked={filteredEmailUsers.length > 0 && emailForm.selectedUsers.length === filteredEmailUsers.length}
                    indeterminate={emailForm.selectedUsers.length > 0 && emailForm.selectedUsers.length < filteredEmailUsers.length}
                    onChange={handleSelectAllEmailUsers}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Select All ({filteredEmailUsers.length})</Typography>
                  <Typography variant="caption" sx={{ ml: 'auto', color: 'text.secondary' }}>{emailForm.selectedUsers.length} selected</Typography>
                </Box>
                <List dense sx={{ maxHeight: 150, overflow: 'auto' }}>
                  {filteredEmailUsers.map((user) => (
                    <ListItem key={user.id} button onClick={() => handleToggleEmailUser(user.id)} sx={{ borderRadius: 1, mb: 0.5, '&:hover': { bgcolor: 'action.hover' } }}>
                      <Checkbox checked={emailForm.selectedUsers.includes(user.id)} onChange={() => handleToggleEmailUser(user.id)} onClick={(e) => e.stopPropagation()} />
                      <ListItemText primary={user.full_name || 'Unknown'} secondary={user.email} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </FormControl>
            <FormControl>
              <FormLabel>Schedule</FormLabel>
              <RadioGroup row value={emailForm.schedule_mode} onChange={(e) => setEmailForm({ ...emailForm, schedule_mode: e.target.value })}>
                <FormControlLabel value="now" control={<Radio />} label="Send now" />
                <FormControlLabel value="schedule" control={<Radio />} label="Schedule for" />
              </RadioGroup>
              {emailForm.schedule_mode === 'schedule' && (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DateTimePicker
                    label="Date & Time"
                    value={emailForm.schedule_at}
                    onChange={(v) => setEmailForm({ ...emailForm, schedule_at: v })}
                    minDateTime={dayjs()}
                    ampm={false}
                    slotProps={{
                      textField: { size: 'small', sx: { mt: 1 } },
                      actionBar: { actions: ['clear', 'accept'] }
                    }}
                  />
                </LocalizationProvider>
              )}
            </FormControl>
            <FormControlLabel
              control={<Checkbox checked={emailForm.send_push} onChange={(e) => setEmailForm({ ...emailForm, send_push: e.target.checked })} />}
              label="Send push notification"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={emailFormLoading}
            startIcon={emailFormLoading ? <CircularProgress size={20} /> : <SendIcon />}
            onClick={handleSendCustomEmail}
            sx={{ background: 'linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)' }}
          >
            {emailFormLoading ? 'Sending...' : emailForm.schedule_mode === 'schedule' ? 'Schedule' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Notification Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => {
          setEditDialogOpen(false);
          setNotificationToEdit(null);
          setEditForm({ title: '', body: '' });
        }} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>Edit Notification</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Title"
              fullWidth
              value={editForm.title}
              onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
            />
            <TextField
              label="Message"
              fullWidth
              multiline
              rows={4}
              value={editForm.body}
              onChange={(e) => setEditForm({ ...editForm, body: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEditDialogOpen(false);
            setNotificationToEdit(null);
            setEditForm({ title: '', body: '' });
          }}>Cancel</Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            sx={{
              background: 'linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)',
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Message Detail Dialog */}
      <Dialog 
        open={messageDialogOpen} 
        onClose={() => setMessageDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          {selectedMessage?.title || 'Notification Details'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              From: {selectedMessage?.user_name || 'System'}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Date: {selectedMessage?.date ? formatDate(selectedMessage.date) : 'N/A'}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              {selectedMessage?.body || ''}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMessageDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NotificationCenter;
